import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { isExplicitSeedViewer } from '@/lib/demoAccountFilters';
import { isCiSmokeBuild } from '@/lib/ciSmoke';

// ── Seed-isolation guard + viewer-relationship tagging ─────────────────────
//
// Profile-view and content-engagement events are classified at write time by
// the actor's relationship to the target org:
//
//   • Drop — the acting user is an explicitly allowlisted seed viewer (same
//     predicate as `useIsSeedViewer`)
//     and the target org is a real (non-seed) org. Seed orgs keep receiving
//     seed-viewer events so demo flows stay alive. Unchanged from PR 3.4.
//   • Team — the acting user is one of the target org's own people: a member
//     (`organization_members`) or an affiliated agent
//     (`profiles.affiliated_org_id`). Recorded and tagged `'team'` so admins
//     see their own people's activity as a separate segment — formerly these
//     were dropped.
//   • External — everyone else, including anonymous visitors. This is the
//     recruiting signal and the headline number on org analytics.
//
// Classification FAILS OPEN to `'external'`: any lookup error records the
// event rather than silently dropping real data. Pollution is recoverable;
// lost real events are not.
//
// Comparison events are out of scope — they keep the PR 3.4 suppression
// behaviour (`shouldSuppressComparison`) for matching-engine integrity.
//
// ── Two rules every tracker in this file obeys (issue #663) ────────────────
//
// 1. A RETURNED ERROR IS A FAILURE. supabase-js reports a failed write in the
//    RESULT OBJECT, not by throwing, so `await supabase.from(...).insert(...)`
//    inside a try/catch swallows every rejection. That is how migration
//    20260414193748's `TO authenticated` + is_published INSERT policies
//    silently discarded anonymous profile views for four months: production
//    still grants `anon` a table-level INSERT, so the write reached RLS,
//    failed 42501, and the surrounding catch never ran. Every tracker now
//    inspects `error` and reports it through `reportAnalyticsFailure`.
//
// 2. A WRITE THAT CANNOT SUCCEED IS SUPPRESSED, NOT ATTEMPTED. The INSERT
//    policies on `profile_view_events` and `content_engagement_events` require
//    the target organization to be PUBLISHED, so an event against a draft is a
//    guaranteed 403. Attempting it would both manufacture Sentry noise and, if
//    it ever did land, contaminate the external recruiting metric with an
//    admin's own preview traffic. Call sites pass the publication state they
//    have already loaded.
//
// Analytics stay non-blocking either way: no tracker throws, none rejects, and
// none shows the user an error. The `AnalyticsOutcome` a tracker returns is
// what lets a test — and a future caller — tell a deliberate suppression apart
// from an attempted write that failed. Callers may ignore it; this file and
// its tests may not.

/**
 * What a tracker did.
 *
 *   recorded    the write was attempted and the database accepted it
 *   suppressed  the write was deliberately NOT attempted (CI smoke, seed
 *               guard, unpublished target, no session where one is required)
 *   failed      the write was attempted and did not land
 */
export type AnalyticsOutcome = 'recorded' | 'suppressed' | 'failed';

/**
 * The non-sensitive signature of a failed Supabase call.
 *
 * Deliberately narrow. A PostgrestError carries `message`, `details` and
 * `hint`, any of which can echo a statement or a value; a FunctionsHttpError
 * carries the whole `Response`. None of that is read. Only a short database
 * error code (e.g. `42501`) and an HTTP status reach telemetry, and both are
 * coerced to a bounded string so a hostile or unexpected shape cannot smuggle
 * a payload into a tag.
 */
function errorSignature(error: unknown): { code: string; status: string } {
  const shape = (error ?? {}) as { code?: unknown; status?: unknown; context?: { status?: unknown } };
  const bounded = (value: unknown): string => {
    if (typeof value === 'number') return String(value);
    if (typeof value !== 'string') return 'unknown';
    // A code is an identifier, never prose. Anything else is discarded rather
    // than truncated, so a message cannot arrive as a "code".
    return /^[A-Za-z0-9_.-]{1,24}$/.test(value) ? value : 'unknown';
  };
  return {
    code: bounded(shape.code),
    status: bounded(shape.status ?? shape.context?.status),
  };
}

/**
 * The one observable failure path. Non-blocking by construction: it reports and
 * returns, never throws, never toasts, never interrupts navigation.
 *
 * `sink` is the table or endpoint name — a constant in this file, never
 * anything derived from user input. No session id, search text, referrer,
 * org id, user id, event payload or credential is attached, here or anywhere
 * else in this module.
 */
function reportAnalyticsFailure(tracker: string, sink: string, error: unknown): 'failed' {
  const { code, status } = errorSignature(error);
  // Fixed text plus two bounded identifiers. The error object itself is never
  // passed to the console, because its `message`/`details`/`hint` can echo a
  // statement.
  console.error(`[analytics] ${tracker} write failed sink=${sink} code=${code} status=${status}`);
  Sentry.captureMessage('[analytics] write failed', {
    level: 'warning',
    tags: { analytics_tracker: tracker, analytics_sink: sink, db_code: code, http_status: status },
  });
  return 'failed';
}

/**
 * Fetch `is_seed` for a set of orgs. Returns a Map keyed by org id. Throws on
 * query error so callers can fail open.
 */
async function fetchOrgSeedFlags(orgIds: string[]): Promise<Map<string, boolean>> {
  const { data, error } = await supabase
    .from('organizations' as any)
    .select('id, is_seed')
    .in('id', orgIds);
  if (error) throw error;
  const flags = new Map<string, boolean>();
  for (const row of (data ?? []) as any[]) flags.set(row.id, !!row.is_seed);
  return flags;
}

/**
 * Of the given orgs, return the subset that `userId` is one of the "own people"
 * for — a CURRENT member (`organization_members`) or an affiliated agent
 * (`profiles.affiliated_org_id`). Throws on query error so callers can fail open.
 *
 * Membership is narrowed to `is_current = true AND end_date IS NULL`, the same
 * rule as the server's read-time `private.is_org_insider`: a former member
 * viewing or comparing the organization is outside interest, not team.
 */
async function orgInsiderSet(userId: string, orgIds: string[]): Promise<Set<string>> {
  const [memberRes, profileRes] = await Promise.all([
    supabase
      .from('organization_members' as any)
      .select('org_id')
      .eq('user_id', userId)
      .eq('is_current', true)
      .is('end_date', null)
      .in('org_id', orgIds),
    supabase
      .from('profiles' as any)
      .select('affiliated_org_id')
      .eq('user_id', userId)
      .maybeSingle(),
  ]);
  if (memberRes.error) throw memberRes.error;
  if (profileRes.error) throw profileRes.error;
  const insiders = new Set<string>();
  for (const row of (memberRes.data ?? []) as any[]) insiders.add(row.org_id);
  const affiliated = (profileRes.data as any)?.affiliated_org_id as string | null | undefined;
  if (affiliated && orgIds.includes(affiliated)) insiders.add(affiliated);
  return insiders;
}

export type ViewerRelationship = 'team' | 'external';

/**
 * Classify a single-org analytics event by the actor's relationship to the
 * target org: `'drop'` (seed guard — skip the insert), `'team'` (one of the
 * org's own people), or `'external'` (everyone else, including anonymous).
 * Fails OPEN: any lookup error returns `'external'` (record).
 *
 * This is the ONLY place a team view is identified. A page must not drop a
 * team member's view before calling a tracker — see
 * docs/profile-views-segmentation-scope.md: the decision is to record it
 * tagged, not to delete it.
 */
async function classifyOrgEvent(
  userId: string | null | undefined,
  orgId: string
): Promise<'drop' | ViewerRelationship> {
  if (!userId) return 'external'; // anonymous visitor = real traffic
  try {
    // Seed guard: seed viewer targeting a positively-real (non-seed) org.
    if (isExplicitSeedViewer(userId)) {
      const seedFlags = await fetchOrgSeedFlags([orgId]);
      if (seedFlags.get(orgId) === false) return 'drop';
    }
    // Insider tag: actor is one of the target org's own people (any seed status).
    const insiders = await orgInsiderSet(userId, [orgId]);
    if (insiders.has(orgId)) return 'team';
    return 'external';
  } catch (e) {
    console.error('[analytics] classification failed, tagging external (fail open):', e);
    return 'external';
  }
}

/**
 * Decide whether a comparison event should be suppressed. A comparison is
 * suppressed when ANY real (non-seed) org in the set is one the actor fails a
 * guard for: a seed viewer comparing a set that includes a real org, or an
 * actor who is a member/affiliate of a real org in the set. Returns `true` to
 * SKIP, `false` to LOG. Fails OPEN.
 */
async function shouldSuppressComparison(userId: string, orgIds: string[]): Promise<boolean> {
  try {
    const seedFlags = await fetchOrgSeedFlags(orgIds);
    const realOrgIds = orgIds.filter((id) => seedFlags.get(id) === false);
    if (realOrgIds.length === 0) return false; // no real org to protect

    // Seed guard: a seed viewer's comparison that includes any real org.
    if (isExplicitSeedViewer(userId)) return true;

    // Self guard: actor is a member/affiliate of any real org in the set.
    const insiders = await orgInsiderSet(userId, realOrgIds);
    if (insiders.size > 0) return true;

    return false;
  } catch (e) {
    console.error('[analytics] comparison suppression guard failed, logging event (fail open):', e);
    return false;
  }
}

// ── Session ID helper (anonymous visitor fingerprint) ──────────────────────
//
// Returns null rather than throwing when there is no usable sessionStorage —
// server-side rendering and the prerender pass have no browser storage at all,
// and a private-mode browser can throw on access. Every consumer accepts a null
// session id, so a missing one costs a unique-visitor de-duplication and
// nothing else.
function getSessionId(): string | null {
  const key = 'cp_session_id';
  try {
    if (typeof sessionStorage === 'undefined') return null;
    let sid = sessionStorage.getItem(key);
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem(key, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

function getTrafficSource(): string {
  if (typeof document === 'undefined') return 'direct';
  const ref = document.referrer;
  if (!ref) return 'direct';
  if (ref.includes('google') || ref.includes('bing') || ref.includes('yahoo')) return 'search';
  if (ref.includes('facebook') || ref.includes('instagram') || ref.includes('linkedin') || ref.includes('twitter')) return 'social';
  if (typeof window !== 'undefined' && window.location?.hostname && ref.includes(window.location.hostname)) return 'direct';
  return 'referral';
}

// ── 1. Profile view (signed-in visitor, direct write under RLS) ─────────────
/**
 * Record an authenticated visitor's view of an organization profile.
 *
 * `options.isPublished` is the publication state the calling page has already
 * loaded. The table's only INSERT policy requires a published organization, so
 * anything else is suppressed before the write rather than sent to be refused —
 * which also keeps an admin previewing their own draft out of the external
 * recruiting metric.
 *
 * An anonymous visitor never reaches this function: the signed-in profile page
 * is behind ProtectedRoute, and the PUBLIC page uses `trackPublicProfileView`.
 */
export async function trackProfileView(
  orgId: string,
  options: { isPublished: boolean },
): Promise<AnalyticsOutcome> {
  if (isCiSmokeBuild()) return 'suppressed';
  try {
    if (options?.isPublished !== true) return 'suppressed';
    const { data: { user } } = await supabase.auth.getUser();
    const relationship = await classifyOrgEvent(user?.id, orgId);
    if (relationship === 'drop') return 'suppressed';
    const { error } = await supabase.from('profile_view_events' as any).insert({
      org_id: orgId,
      viewer_user_id: user?.id ?? null,
      session_id: getSessionId(),
      traffic_source: getTrafficSource(),
      referrer_url: document.referrer || null,
      page_path: window.location.pathname,
      viewer_relationship: relationship,
    } as any);
    if (error) return reportAnalyticsFailure('trackProfileView', 'profile_view_events', error);
    return 'recorded';
  } catch (e) {
    return reportAnalyticsFailure('trackProfileView', 'profile_view_events', e);
  }
}

// ── 1b. Profile view (anonymous visitor, via the trusted endpoint) ──────────
/**
 * Record an ANONYMOUS visitor's view of a published, public organization
 * profile — the external recruiting signal on `/organization/:slug`.
 *
 * This is a separate function from `trackProfileView` on purpose. It is not a
 * table write at all: direct `anon` INSERT on the analytics base tables stays
 * unavailable, so the row is written by the `record-public-profile-view` Edge
 * Function, which resolves the organization server-side from the slug and owns
 * `org_id`, `viewer_user_id` and `viewer_relationship` itself. Overloading
 * `trackProfileView` with a hidden privilege difference would hide exactly that.
 *
 * The endpoint answers identically for an unknown, unpublished, seed or unowned
 * slug, so this function cannot be used to probe organization state either.
 *
 * Never runs outside a browser: the prerender pass renders this page under Node
 * with no effects, and this guard makes that explicit rather than incidental.
 */
export async function trackPublicProfileView(slug: string): Promise<AnalyticsOutcome> {
  if (isCiSmokeBuild()) return 'suppressed';
  try {
    if (typeof window === 'undefined') return 'suppressed';
    if (!slug) return 'suppressed';
    const { data, error } = await supabase.functions.invoke('record-public-profile-view', {
      body: {
        slug,
        sessionId: getSessionId(),
        trafficSource: getTrafficSource(),
        referrer: typeof document === 'undefined' ? null : document.referrer || null,
      },
    });
    if (error) {
      return reportAnalyticsFailure('trackPublicProfileView', 'record-public-profile-view', error);
    }
    // A 2xx is not the same as a row. The endpoint answers `recorded: false`
    // for every organization-state outcome — unknown, hidden, seed, unpublished,
    // unowned, and a lookup that failed — deliberately collapsed into ONE reply
    // so a caller cannot probe which. That is a deliberate no-write, so it maps
    // to `suppressed`, and nothing about which case it was reaches the client or
    // telemetry. Anything that is NOT one of the two contract shapes is a
    // contract violation, not a suppression, and is reported as a failure.
    const payload = data as { ok?: unknown; recorded?: unknown } | null | undefined;
    if (!payload || typeof payload !== 'object' || payload.ok !== true || typeof payload.recorded !== 'boolean') {
      return reportAnalyticsFailure('trackPublicProfileView', 'record-public-profile-view', {
        code: 'malformed_response',
      });
    }
    return payload.recorded ? 'recorded' : 'suppressed';
  } catch (e) {
    return reportAnalyticsFailure('trackPublicProfileView', 'record-public-profile-view', e);
  }
}

// ── 2. Agent interaction ─────────────────────────────────────────────────────
export type InteractionType =
  | 'save' | 'unsave' | 'contact_click' | 'schedule_click'
  | 'booking' | 'message_sent' | 'phone_reveal' | 'website_click';

export async function trackInteraction(
  orgId: string,
  eventType: InteractionType,
  metadata: Record<string, unknown> = {}
): Promise<AnalyticsOutcome> {
  if (isCiSmokeBuild()) return 'suppressed';
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 'suppressed'; // interactions require auth
    const { error } = await supabase.from('agent_interaction_events' as any).insert({
      org_id: orgId,
      agent_user_id: user.id,
      event_type: eventType,
      metadata,
    } as any);
    if (error) return reportAnalyticsFailure('trackInteraction', 'agent_interaction_events', error);
    return 'recorded';
  } catch (e) {
    return reportAnalyticsFailure('trackInteraction', 'agent_interaction_events', e);
  }
}

// ── 3. Comparison session ────────────────────────────────────────────────────
export async function trackComparison(
  orgIds: string[],
  selectedOrgId?: string
): Promise<AnalyticsOutcome> {
  if (isCiSmokeBuild()) return 'suppressed';
  try {
    const { data: { user } } = await supabase.auth.getUser();
    // The table's INSERT policy is `initiator_user_id = auth.uid()`, so an
    // anonymous comparison has always been refused. Suppressing it changes no
    // row that was ever written; it only stops a guaranteed rejection being
    // reported as a failure.
    if (!user) return 'suppressed';
    if (orgIds.length > 0 && await shouldSuppressComparison(user.id, orgIds)) return 'suppressed';
    const { error } = await supabase.from('comparison_events' as any).insert({
      initiator_user_id: user.id,
      session_id: getSessionId(),
      org_ids: orgIds,
      selected_org_id: selectedOrgId ?? null,
    } as any);
    if (error) return reportAnalyticsFailure('trackComparison', 'comparison_events', error);
    return 'recorded';
  } catch (e) {
    return reportAnalyticsFailure('trackComparison', 'comparison_events', e);
  }
}

// ── 4. Search event ──────────────────────────────────────────────────────────
/**
 * Record that a signed-in searcher ACTIVATED one concrete organization result
 * for a non-empty query — `was_clicked = true`, with the result's one-based
 * position in the sorted set the searcher was actually looking at.
 *
 * Callers go through `src/lib/exploreSearchActivation.ts`, which owns the
 * "exactly once per activation" rule. Nothing here fires on a keystroke, a
 * render, or an empty query.
 *
 * Explore only ever lists `is_published = true` organizations, so the INSERT
 * policy's publication requirement is satisfied by the result set itself and
 * needs no separate flag.
 */
export async function trackSearch(
  orgId: string,
  query: string,
  resultPosition: number,
  wasClicked: boolean
): Promise<AnalyticsOutcome> {
  if (isCiSmokeBuild()) return 'suppressed';
  try {
    const { data: { user } } = await supabase.auth.getUser();
    // The INSERT policy is `TO authenticated`; without a session this is a
    // guaranteed 42501, so do not send it.
    if (!user) return 'suppressed';
    const { error } = await supabase.from('search_events' as any).insert({
      org_id: orgId,
      searcher_user_id: user.id,
      session_id: getSessionId(),
      query,
      result_position: resultPosition,
      was_clicked: wasClicked,
    } as any);
    if (error) return reportAnalyticsFailure('trackSearch', 'search_events', error);
    return 'recorded';
  } catch (e) {
    return reportAnalyticsFailure('trackSearch', 'search_events', e);
  }
}

// ── 5. Content engagement ────────────────────────────────────────────────────
export type ContentType =
  | 'section_view' | 'photo_view' | 'video_play' | 'video_complete'
  | 'document_view' | 'post_view' | 'post_like' | 'post_comment';

/**
 * Record an authenticated visitor's engagement with a piece of organization
 * content. Like `trackProfileView`, the table's INSERT policy requires a
 * published organization, so `options.isPublished` gates the write.
 *
 * There is deliberately no anonymous counterpart. The public profile page
 * renders no content-engagement surface — no video player, no section tracking —
 * so inventing one would be an expansion the evidence does not support.
 */
export async function trackContentEngagement(
  orgId: string,
  contentType: ContentType,
  options: {
    isPublished: boolean;
    sectionId?: string;
    contentId?: string;
    dwellSeconds?: number;
  },
): Promise<AnalyticsOutcome> {
  if (isCiSmokeBuild()) return 'suppressed';
  try {
    if (options?.isPublished !== true) return 'suppressed';
    const { data: { user } } = await supabase.auth.getUser();
    const relationship = await classifyOrgEvent(user?.id, orgId);
    if (relationship === 'drop') return 'suppressed';
    const { error } = await supabase.from('content_engagement_events' as any).insert({
      org_id: orgId,
      viewer_user_id: user?.id ?? null,
      session_id: getSessionId(),
      content_type: contentType,
      section_id: options.sectionId ?? null,
      content_id: options.contentId ?? null,
      dwell_seconds: options.dwellSeconds ?? null,
      viewer_relationship: relationship,
    } as any);
    if (error) {
      return reportAnalyticsFailure('trackContentEngagement', 'content_engagement_events', error);
    }
    return 'recorded';
  } catch (e) {
    return reportAnalyticsFailure('trackContentEngagement', 'content_engagement_events', e);
  }
}
